import React, { useEffect, useState, useCallback } from 'react';
import cN from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { setFavList, setFavPopUp } from '@Redux/search/actions';
import { RootState } from '@Redux/search/store';
import { I_CardsInfo_Common } from '@Redux/search/interfaces';
import { useMediaQuery } from 'react-responsive';
import * as Tag from '@components/Common/Tag';
import * as Icons from '@components/Common/Icons';
import { formatPrice } from '@utils/utils';
import { handleFavorite } from '@utils/favorite';
import ArrowRight3 from '@lion-design/icons';
import defaultImg from '@utils/defaultImg';
import styles from '../styles.module.scss';

interface I_Props {
    groupData: I_CardsInfo_Common;
    isLogin: boolean;
}

const CardDetail = (props: any) => {
    const isMobile = useMediaQuery({ query: '(max-width: 980px)' });
    const { Place, DestinationCountry, DestinationCity, AllowDate, SubType, TransportType, GoDateShow, AirportCabin } = props;

    let text = null;
    //1：團體 2：團體自由行 3：訂房 4：機票 5：個人自由行 7：票券/當地遊  8：台灣自由行
    switch (SubType) {
        case 1 :
        case 2 :
            if (GoDateShow !== '') {
                const array = GoDateShow.split('、');
                const date = array.slice(0, 3);
                const moreDate = date.join('、');
                if (array.length > 3) {
                    text = (
                        <span>
                            {isMobile ? `${moreDate}...` : `出發日期：${moreDate}...`}
                        </span>
                    );
                } else {
                    text = <span>{isMobile ? moreDate : '出發日期：' + moreDate}</span>;
                }
            }
            break;
        case 3:
            text = (
                <>
                    <span className={styles.placeText_icon}>
                        <Icons.Pin width="9" height="12" fill="#fff" stroke="#666" />
                    </span>
                    {!isMobile && DestinationCountry + ' - '} {DestinationCity}
                </>
            );
            break;
        case 4:
            text = (
                <>
                    <span>
                        {AirportCabin ? AirportCabin : null}
                    </span>
                </>
            );
            break;
        case 7:    
            text = (
                <>
                    {DestinationCountry}
                    {DestinationCity ? ' - ' + DestinationCity : null} | 最早可使用期限：
                    {AllowDate ? AllowDate : '詳見產品說明'}
                </>
            );
            break;
        case 5:
        case 8:
            text = <span>{TransportType ? '交通方式 : ' + TransportType : null}</span>; //這裡資料目前是字串而非陣列字串
            break;
        default: 
    }
    return (
        <div className={styles.placeTextBox}>
            <div className={styles.placeText}>{text}</div>
        </div>
    );
};

const Card: React.FC<I_Props> = ({ groupData, isLogin }) => {
    const {
        ProductURL,
        ImageURL,
        ProductName,
        ProductType,
        DirectPrice,
        TradePrice,
        ProductID,
        SubType,
        IsVip,
        GoDate, // 團體館 需要顯示出發日期用
        AirportCabin, //機票的艙等
    } = groupData; // 這邊傳入資料可能要更動，大多數應該是不必要
    const { TagList, favList, favPopUp } = useSelector((state: RootState) => state.search.reducer);
    const isMobile = useMediaQuery({ query: '(max-width: 980px)' });
    const [isFavorite, setIsFavorite] = useState<boolean>(false);
    const dispatch = useDispatch();
    const productLink =
        SubType === 1 || SubType === 2 ? `${ProductURL}&GroupID=${ProductID}` : ProductURL; // 團的收藏是用GroupID，網址帶GroupID進去的頁面才會顯示正確的收藏狀況

    // 收藏，
    useEffect(() => {
        setIsFavorite(
            Boolean(favList?.find((favItem: any) => favItem.ProductID === ProductID)) || false
        );
    }, [favList]);

    const triggerFav = async () => {
        const response = await handleFavorite({
            isLogin,
            isFavorite: isFavorite,
            TravelType: SubType,
            TagList,
            ProductID: ProductID,
            ProductName: ProductName,
            Price: TradePrice,
            ProductURL: ProductURL,
            GoDate: GoDate,
            AirportCabin: AirportCabin,
        });

        if (response === false) return; //請求失敗，依舊是原本的值
        const prevValue = [...favList];
        const selected = prevValue.findIndex((favItem: any) => favItem.ProductID === ProductID);
        if (selected > -1) {
            prevValue.splice(selected, 1);
            dispatch(setFavPopUp('unFav'));
            dispatch(setFavList(prevValue));
        } else {
            prevValue.push({
                ProductID: ProductID,
                ProductName: ProductName,
                TravelType: SubType,
            });
            dispatch(setFavPopUp('addFav'));
            dispatch(setFavList(prevValue));
        }
        setIsFavorite((prev) => !prev);
    };

    // 登入後重新發送請求再觸發一次加入收藏
    useEffect(() => {
        if (isLogin) {
            if (sessionStorage.getItem('waitedForAddedFav') === ProductID) {
                triggerFav();
                sessionStorage.removeItem('waitedForAddedFav');
            }
        }
    }, [isLogin]);

    return (
        <li>
            <a className={styles.card} href={productLink} target="_blank" rel="noreferrer noopener">
                {IsVip === 1 && isMobile && <div className={styles.onlyVip}>會員限購</div>}
                <div className={styles.cardContent}>
                    <div className={styles.cardImgWrap}>
                        {ImageURL !== null &&
                        ImageURL.length > 0 &&
                        ImageURL.match(/\.jpg|.jpeg|.png|.gif|\.webp/i) ? (
                            <img
                                className={styles.cardImg}
                                src={ImageURL}
                                alt={ProductName}
                                onError={(e: any) => (e.target.src = defaultImg)}
                            />
                        ) : (
                            <img className={styles.cardImg} src={defaultImg} alt={ProductName} />
                        )}
                    </div>
                    <div className={styles.cardInfoWrap}>
                        <div className={styles.cardHeader}>
                            <div className={styles.tagWrap}>
                                {/* ProductType 只有所有館別才有這參數 */}
                                <Tag.Featured text={ProductType ? ProductType : TagList} />
                            </div>

                            <div className={styles.buttons}>
                                <div
                                    onClick={(e) => {
                                        e.preventDefault(); //收藏按鈕
                                        triggerFav();
                                    }}
                                    className={cN(styles.button, { [styles.active]: isFavorite })}
                                >
                                    {isFavorite ? (
                                        <Icons.FillHeart fill="#f03742" width="13" height="13" />
                                    ) : (
                                        <Icons.HollowHeart fill="#999" width="13" height="13" />
                                    )}
                                    <span>收藏</span>
                                </div>
                            </div>
                        </div>
                        <div className={styles.cardInfoMain}>
                            <div className={styles.caption}>
                                <h3 className={styles.cardTitle}>{ProductName}</h3>
                                <CardDetail {...groupData} />
                            </div>
                            <span className={styles.price}>
                            {AirportCabin? <span className={styles.priceTaxed}>大人含稅價</span> : null}
                                {IsVip === 1 && !isMobile && (
                                    <span className={styles.onlyVip}>會員限購</span>
                                )}
                                {DirectPrice > TradePrice && (
                                    <div className={styles.origPrice}>
                                        <span data-unit="TWD ">{formatPrice(DirectPrice)}</span>
                                    </div>
                                )}
                                

                                {Number(TradePrice) === 0 ? (
                                    <span className={cN(styles.noPrice)}>
                                        <span>查看價格</span>
                                        <span className={cN(styles.noPrice_arrow)}>
                                            <ArrowRight3 type="arrow-right3" />
                                        </span>
                                    </span>
                                ) : (
                                    <div
                                        data-currency="TWD"
                                        data-unit="起"
                                        className={cN(styles.sellPrice)}
                                    >
                                        {formatPrice(TradePrice)}
                                    </div>
                                )}
                            </span>
                        </div>
                    </div>
                </div>
            </a>
        </li>
    );
};

export default Card;
